/**
 * Integrytas Group - Data Quality Engine (DQE)
 * Breach-scan data/logic layer — shared by the desktop admin page and the
 * mobile-native page (§16: business logic lives ONCE; each surface renders its
 * own presentation over this same layer).
 *
 * Boundaries honoured here:
 *   - READS (snapshots, scan, drill-through rows) go through `apiDb` — the scan
 *     and drill-through are read-only `kind:'rpc'` calls to SECURITY INVOKER
 *     Postgres functions, so RLS is applied as the calling admin (§1, §5).
 *   - WRITES (persisting the snapshot) funnel through the Common Routine via
 *     `routedInsert`/`routedUpdate` — gateway `kind:'mutate'` (§2). Never a
 *     direct `apiDb` write.
 */

import { apiDb } from './api_client';
import { routedInsert, routedUpdate } from './routed_writes';
import type { AuthSession } from '../types/auth';

const SNAPSHOT_TABLE = 'dqe_breach_snapshot';
const SNAPSHOT_PK = 'dqe_breach_snapshot_id';
/** Drill-through cap — matches the server-side LEAST()/GREATEST() clamp. */
export const BREACH_ROWS_DEFAULT_LIMIT = 100;

/** One persisted per-rule breach snapshot (latest scan). */
export interface BreachSnapshot {
  dqe_breach_snapshot_id: number;
  data_quality_rule_id: number;
  breach_count: number;
  is_evaluable: 'Y' | 'N';
  last_scanned_at: string;
}

/** A single rule's fresh count as returned by the scan rpc. */
interface ScanResultRow {
  data_quality_rule_id: number;
  breach_count: number;
  is_evaluable: 'Y' | 'N';
}

interface ScanRpcResult {
  scanned_at: string;
  results: ScanResultRow[];
}

/** Aggregate figures for the summary banner / KPI row. */
export interface BreachSummary {
  rulesBreaching: number;
  totalBreaches: number;
  /** ISO timestamp of the most recent scan across all snapshots, or null. */
  lastScannedAt: string | null;
}

export interface RoutedishError {
  message: string;
  code?: string;
}

/** The breaching rows for one rule (drill-through). */
export interface BreachRows {
  entity_name: string | null;
  is_evaluable: 'Y' | 'N';
  rows: Record<string, unknown>[];
}

/** Load every persisted snapshot, keyed by rule id for O(1) join in the UI. */
export async function loadBreachSnapshots(): Promise<{
  byRule: Map<number, BreachSnapshot>;
  error: RoutedishError | null;
}> {
  const { data, error } = await apiDb
    .from(SNAPSHOT_TABLE)
    .select('dqe_breach_snapshot_id, data_quality_rule_id, breach_count, is_evaluable, last_scanned_at');
  if (error) return { byRule: new Map(), error };
  const byRule = new Map<number, BreachSnapshot>();
  for (const s of (data ?? []) as BreachSnapshot[]) byRule.set(s.data_quality_rule_id, s);
  return { byRule, error: null };
}

/** Derive the banner/KPI aggregates from a set of snapshots. */
export function summariseBreaches(snapshots: Iterable<BreachSnapshot>): BreachSummary {
  let rulesBreaching = 0;
  let totalBreaches = 0;
  let lastScannedAt: string | null = null;
  for (const s of snapshots) {
    if (s.is_evaluable === 'Y' && s.breach_count > 0) rulesBreaching += 1;
    if (s.is_evaluable === 'Y') totalBreaches += s.breach_count;
    if (!lastScannedAt || s.last_scanned_at > lastScannedAt) lastScannedAt = s.last_scanned_at;
  }
  return { rulesBreaching, totalBreaches, lastScannedAt };
}

/**
 * Run the breach scan, then persist one snapshot per rule through the Common
 * Routine (ADD when no snapshot exists yet for the rule, CHANGE otherwise —
 * the snapshot is UNIQUE per rule). Returns the reloaded snapshots so the caller
 * renders canonical persisted values. Stops and surfaces the first write error.
 */
export async function runBreachScan(session: AuthSession): Promise<{
  byRule: Map<number, BreachSnapshot>;
  scannedAt: string | null;
  error: RoutedishError | null;
}> {
  // (1) Compute — read-only rpc, RLS-respecting.
  const { data, error: scanError } = await apiDb.rpc<ScanRpcResult>('dqe_scan_breaches');
  if (scanError) return { byRule: new Map(), scannedAt: null, error: scanError };
  const scan = data as ScanRpcResult | null;
  if (!scan) return { byRule: new Map(), scannedAt: null, error: { message: 'DQE.SCAN.EMPTY_RESULT' } };

  // (2) Know which rules already have a snapshot (ADD vs CHANGE).
  const { byRule: existing, error: loadError } = await loadBreachSnapshots();
  if (loadError) return { byRule: existing, scannedAt: scan.scanned_at, error: loadError };

  // (3) Persist each result through the Common Routine (§2).
  for (const r of scan.results) {
    const current = existing.get(r.data_quality_rule_id);
    const patch = {
      breach_count: r.breach_count,
      is_evaluable: r.is_evaluable,
      last_scanned_at: scan.scanned_at,
    };
    const { error } = current
      ? await routedUpdate(session, SNAPSHOT_TABLE, SNAPSHOT_PK, current.dqe_breach_snapshot_id, patch)
      : await routedInsert(session, SNAPSHOT_TABLE, { data_quality_rule_id: r.data_quality_rule_id, ...patch });
    if (error) {
      return { byRule: existing, scannedAt: scan.scanned_at, error };
    }
  }

  // (4) Reload canonical persisted snapshots.
  const reloaded = await loadBreachSnapshots();
  return { byRule: reloaded.byRule, scannedAt: scan.scanned_at, error: reloaded.error };
}

/** Fetch the actual breaching rows for one rule (drill-through), capped server-side. */
export async function loadBreachRows(
  ruleId: number,
  limit = BREACH_ROWS_DEFAULT_LIMIT,
): Promise<{ data: BreachRows | null; error: RoutedishError | null }> {
  const { data, error } = await apiDb.rpc<BreachRows>('dqe_rule_breach_rows', {
    p_rule_id: ruleId,
    p_limit: limit,
  });
  if (error) return { data: null, error };
  return { data: (data as BreachRows) ?? { entity_name: null, is_evaluable: 'N', rows: [] }, error: null };
}

/**
 * Choose the columns to surface for a breaching row (§17.B4: never lead with raw
 * surrogate ids). Puts the rule's referenced column(s) first, then a short set of
 * natural identifiers / other columns, and omits pure surrogate keys.
 * fk-display-ok: this is a diagnostic view of the ACTUAL offending source records;
 * surrogate PKs identify nothing to a human and are intentionally hidden, while the
 * rule's own columns (the reason the row breaches) are shown first.
 */
export function pickBreachColumns(
  rows: Record<string, unknown>[],
  ruleColumns: string[],
  max = 6,
): string[] {
  if (rows.length === 0) return ruleColumns.slice(0, max);
  const present = Object.keys(rows[0]);
  const isSurrogate = (k: string) => k === 'id' || /_id$/.test(k);
  const naturalFirst = ['internal_code', 'code', 'name', 'display_name', 'title', 'email'];

  const ordered: string[] = [];
  const push = (k: string) => {
    if (present.includes(k) && !ordered.includes(k) && ordered.length < max) ordered.push(k);
  };
  ruleColumns.forEach(push);          // the offending column(s) first
  naturalFirst.forEach(push);         // then human-readable identifiers
  for (const k of present) {          // then any remaining non-surrogate, non-audit column
    if (!isSurrogate(k) && k !== 'created_at' && k !== 'updated_at' && k !== 'modified_by') push(k);
  }
  return ordered;
}

/** Extract the column names a rule references (for drill-through display ordering). */
export function ruleReferencedColumns(ruleType: string, ruleDef: unknown): string[] {
  const def = (ruleDef ?? {}) as Record<string, unknown>;
  if (ruleType === 'REQUIRED' || ruleType === 'FORMAT') {
    return typeof def.column === 'string' ? [def.column] : [];
  }
  if (ruleType === 'CROSS_FIELD') {
    const pred = (def.predicate ?? {}) as Record<string, unknown>;
    if (pred.type === 'compare') {
      return [pred.left_field, pred.right_field].filter((v): v is string => typeof v === 'string');
    }
    if (pred.type === 'require_if') {
      const when = (pred.when ?? {}) as Record<string, unknown>;
      const requires = Array.isArray(pred.require) ? (pred.require as unknown[]) : [];
      return [when.field, ...requires].filter((v): v is string => typeof v === 'string');
    }
  }
  return [];
}
